import type { ContextMessage } from '../types/chat'

import { ContextUpdateStrategy } from '@proj-airi/server-sdk'

// Electron main loads this file as native ESM. The specifier needs the .ts extension.
import { frameEvidence } from './velis-evidence.ts'

export const memoryKinds = ['Profile', 'Episodic', 'Task'] as const
export const memoryStatuses = ['active', 'superseded', 'deleted'] as const

export type MemoryKind = typeof memoryKinds[number]
export type MemoryStatus = typeof memoryStatuses[number]

export interface MemoryProvenance {
  sessionId: string
  source: 'user'
  recordedAt: string
}

export interface MemoryRecord {
  id: string
  kind: MemoryKind
  status: MemoryStatus
  content: string
  topic: string
  provenance: MemoryProvenance
  /** Active character when the record was written. Missing means the record is shared. */
  characterId?: string
  supersedesId?: string
  supersededById?: string
}

export interface MemoryTrace {
  action: 'WRITE' | 'SUPERSEDE' | 'FORGET' | 'SKIP'
  memoryId?: string
  source: 'user'
  sessionId: string
  reason: string
}

export interface MemoryDecision {
  trace: MemoryTrace
  record?: MemoryRecord
  replacedRecordId?: string
}

const credentialTerms = ['api key', 'access token', 'refresh token', 'password', 'secret', 'bearer', '密码', '令牌', '密钥']
const greetings = ['hi', 'hello', 'hey', '你好', '您好', '嗨']

function explicitBody(message: string, markers: string[]): string | undefined {
  const marker = markers.find(item => message.startsWith(item))
  if (!marker)
    return undefined
  const body = message.slice(marker.length).trim()
  return body || undefined
}

function explicitCorrection(message: string): { previousContent: string, correctedValue: string } | undefined {
  const english = explicitBody(message, ['Correction:'])
  if (english?.includes(' is ')) {
    const [previousContent, correctedValue] = english.split(' is ', 2).map(value => value.trim())
    if (previousContent && correctedValue)
      return { previousContent, correctedValue }
  }

  const chinese = message.match(/^纠正[:：]\s*(.+?)\s*(?:改为|改成)\s*(.+)$/)
  if (!chinese?.[1] || !chinese[2])
    return undefined
  return { previousContent: chinese[1].trim(), correctedValue: chinese[2].trim() }
}

function normalizeTopic(content: string): string {
  return content.toLocaleLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim().split(' ').slice(0, 6).join(' ')
}

function createRecord(input: {
  content: string
  id: string
  kind: MemoryKind
  now: string
  sessionId: string
  characterId?: string
  supersedesId?: string
  topic?: string
}): MemoryRecord {
  return {
    ...(input.characterId ? { characterId: input.characterId } : {}),
    content: input.content,
    id: input.id,
    kind: input.kind,
    provenance: { recordedAt: input.now, sessionId: input.sessionId, source: 'user' },
    status: 'active',
    supersedesId: input.supersedesId,
    topic: input.topic ?? normalizeTopic(input.content),
  }
}

/** Decides one explicit user-memory operation without calling a model. */
export function decideMemory(input: {
  createId: () => string
  message: string
  now: string
  records: MemoryRecord[]
  sessionId: string
  characterId?: string
}): MemoryDecision {
  const message = input.message.trim()
  const baseTrace = { source: 'user' as const, sessionId: input.sessionId }

  if (!message || greetings.includes(message.replace(/[!！,.，\s]/g, '').toLocaleLowerCase()))
    return { trace: { ...baseTrace, action: 'SKIP', reason: 'greeting-or-empty' } }

  if (credentialTerms.some(term => message.toLocaleLowerCase().includes(term)))
    return { trace: { ...baseTrace, action: 'SKIP', reason: 'contains-credential' } }

  const forgetTarget = explicitBody(message, ['Forget ', '忘记：', '忘记:', '忘记 ', '忘掉：', '忘掉:', '忘掉 '])
  if (forgetTarget) {
    const target = normalizeTopic(forgetTarget)
    const active = input.records.find(record => record.status === 'active' && (record.topic === target || record.content.includes(forgetTarget)))
    if (!active)
      return { trace: { ...baseTrace, action: 'SKIP', reason: 'forget-target-not-found' } }

    return { trace: { ...baseTrace, action: 'FORGET', memoryId: active.id, reason: 'explicit-forget' }, replacedRecordId: active.id }
  }

  const correction = explicitCorrection(message)
  if (correction) {
    const { previousContent, correctedValue } = correction
    const previous = input.records.find(record => record.status === 'active' && record.content.includes(previousContent))
    if (!previous)
      return { trace: { ...baseTrace, action: 'SKIP', reason: 'correction-target-not-found' } }

    const id = input.createId()
    const content = previous.content.replace(previousContent, correctedValue)
    const record = createRecord({ characterId: input.characterId, content, id, kind: previous.kind, now: input.now, sessionId: input.sessionId, supersedesId: previous.id, topic: previous.topic })
    return { trace: { ...baseTrace, action: 'SUPERSEDE', memoryId: id, reason: 'explicit-correction' }, record, replacedRecordId: previous.id }
  }

  const taskContent = explicitBody(message, ['Current task:', '当前任务：', '当前任务:'])
  const factContent = explicitBody(message, ['Remember:', '记住：', '记住:', '记住 '])
  if (!taskContent && !factContent)
    return { trace: { ...baseTrace, action: 'SKIP', reason: 'not-an-explicit-fact' } }

  const content = taskContent ?? factContent!
  const episodicTerms = ['yesterday', 'today', 'completed', 'happened', '昨天', '今天']
  const kind: MemoryKind = taskContent ? 'Task' : episodicTerms.some(term => content.toLocaleLowerCase().includes(term)) ? 'Episodic' : 'Profile'
  const activeTask = kind === 'Task' ? input.records.find(record => record.status === 'active' && record.kind === 'Task') : undefined
  const id = input.createId()
  const record = createRecord({
    characterId: input.characterId,
    content,
    id,
    kind,
    now: input.now,
    sessionId: input.sessionId,
    supersedesId: activeTask?.id,
    topic: kind === 'Task' ? 'current-task' : undefined,
  })
  const action = activeTask ? 'SUPERSEDE' : 'WRITE'
  const reason = kind === 'Task'
    ? 'explicit-current-task'
    : kind === 'Episodic'
      ? 'explicit-episodic-fact'
      : 'explicit-fact'
  return { trace: { ...baseTrace, action, memoryId: id, reason }, record, replacedRecordId: activeTask?.id }
}

/** Applies a decision to the semantic record set. */
export function applyMemoryDecision(records: MemoryRecord[], decision: MemoryDecision): MemoryRecord[] {
  const replacedId = decision.replacedRecordId
  const replaced = replacedId
    ? records.map((record) => {
        if (record.id !== replacedId)
          return record
        return decision.trace.action === 'FORGET'
          ? { ...record, status: 'deleted' as const }
          : { ...record, status: 'superseded' as const, supersededById: decision.record?.id }
      })
    : records

  return decision.record ? [...replaced, decision.record] : replaced
}

function queryTerms(query: string) {
  const words = normalizeTopic(query).split(' ').filter(term => term.length >= 2)
  const overlaps = words.flatMap((word) => {
    if (!/\p{Script=Han}/u.test(word) || word.length < 3)
      return []
    return Array.from({ length: word.length - 1 }, (_, index) => word.slice(index, index + 2))
  })
  return [...words, ...overlaps]
}

/** Local desktop user id stored with each Mem0 vector. */
export const velisMemoryUserId = 'local'

export interface MemorySearchHit {
  memoryId: string
  score?: number
}

/** How one send chooses records for the `velis:memory` bucket. */
export type MemoryRecall
  = { source: 'lexical' }
    | { source: 'mem0', hits: readonly MemorySearchHit[] }
    | { source: 'skipped' }

export interface MemoryIndex {
  remove: (memoryId: string) => Promise<void>
  upsert: (record: MemoryRecord) => Promise<void>
}

function budgetMemory(records: MemoryRecord[], ranked: readonly MemoryRecord[]) {
  const profile = ranked.filter(record => record.kind === 'Profile').slice(0, 3)
  const episodic = ranked.filter(record => record.kind === 'Episodic').slice(0, 3)
  const task = records.filter(record => record.status === 'active' && record.kind === 'Task').slice(0, 1)
  const selected = [...profile, ...episodic, ...task]
  const text = selected.map(record => `[${record.kind}] ${record.content}`).join('\n').slice(0, 1200)
  return { records: selected, text }
}

/** Retrieves active records with the V1 lexical matching and context budgets. */
export function retrieveMemory(records: MemoryRecord[], query: string) {
  const terms = queryTerms(query)
  const relevant = records.filter((record) => {
    if (record.status !== 'active')
      return false
    return terms.length === 0 || terms.some(term => record.content.toLocaleLowerCase().includes(term))
  })
  return budgetMemory(records, relevant.filter(record => record.kind !== 'Task'))
}

/**
 * Keeps Mem0 hits that are still active in the semantic file.
 * Superseded and deleted records stay out of the prompt.
 */
export function rankMemoryHits(records: readonly MemoryRecord[], hits: readonly MemorySearchHit[]): MemoryRecord[] {
  const active = new Map(records.filter(record => record.status === 'active').map(record => [record.id, record]))
  const ranked: MemoryRecord[] = []
  const seen = new Set<string>()
  for (const hit of hits) {
    const record = active.get(hit.memoryId)
    if (!record || seen.has(record.id) || record.kind === 'Task')
      continue
    seen.add(record.id)
    ranked.push(record)
  }
  return ranked
}

/**
 * Writes one semantic decision into the search index.
 * Skip does not touch the index. A replacement removes the old id first.
 */
export async function syncMemoryIndex(index: MemoryIndex, decision: MemoryDecision): Promise<void> {
  if (decision.trace.action === 'SKIP')
    return
  if (decision.replacedRecordId)
    await index.remove(decision.replacedRecordId)
  if (decision.record?.status === 'active')
    await index.upsert(decision.record)
}

/** Creates the temporary context bucket that the chat runtime replaces before every request. */
export function createMemoryContext(records: MemoryRecord[], query: string, recall: MemoryRecall = { source: 'lexical' }): ContextMessage | undefined {
  if (recall.source === 'skipped')
    return undefined
  const result = recall.source === 'mem0'
    ? budgetMemory(records, rankMemoryHits(records, recall.hits))
    : retrieveMemory(records, query)
  const text = frameEvidence('memory', result.text)
  if (!text)
    return undefined
  return { id: 'velis:memory:context', contextId: 'velis:memory', strategy: ContextUpdateStrategy.ReplaceSelf, metadata: { source: { id: 'velis:memory' } }, text, createdAt: Date.now() }
}

export interface MemoryTurnClock {
  readStartedAt: number
  retrievalReadyAt: number
}

export interface PreparedMemoryTurn {
  sessionId: string
  records: MemoryRecord[]
  query: string
  decision: MemoryDecision
  recall: MemoryRecall
  phase: 'queued' | 'prompted'
  clock?: MemoryTurnClock
  latencyBound?: boolean
}

/**
 * Correlates one prepared retrieval with the later prompt and write.
 * Sends enter in order. The chat queue runs one send at a time.
 * A settled send drops its own prompted turn when no assistant message was stored.
 */
export function createMemoryTurnBuffer() {
  const turns: PreparedMemoryTurn[] = []

  function push(turn: Omit<PreparedMemoryTurn, 'phase' | 'recall'> & { recall?: MemoryRecall }) {
    turns.push({ ...turn, phase: 'queued', recall: turn.recall ?? { source: 'lexical' } })
  }

  function takeForPrompt() {
    const turn = turns.find(item => item.phase === 'queued')
    if (!turn)
      return undefined
    turn.phase = 'prompted'
    return turn
  }

  function takeForWrite(sessionId: string) {
    const index = turns.findIndex(item => item.sessionId === sessionId && item.phase === 'prompted')
    if (index < 0)
      return undefined
    const [turn] = turns.splice(index, 1)
    return turn?.decision
  }

  function dropPrompted(sessionId: string) {
    const index = turns.findIndex(item => item.sessionId === sessionId && item.phase === 'prompted')
    if (index >= 0)
      turns.splice(index, 1)
  }

  function takePromptedClock(sessionId: string) {
    const turn = turns.find(item => item.sessionId === sessionId && item.phase === 'prompted' && !item.latencyBound)
    if (!turn?.clock)
      return undefined
    turn.latencyBound = true
    return turn.clock
  }

  return { push, takeForPrompt, takeForWrite, dropPrompted, takePromptedClock }
}
